import { useEffect, useState } from "react";

/**
 * `useIsMobile` — true when the viewport is iPhone/Android width.
 *
 * Breakpoint: 640px (Tailwind's `sm`). Below that we render the mobile
 * shell; above that the desktop layout.
 *
 * We listen to `resize` (covers rotation + DevTools device-toolbar)
 * AND to the matchMedia change event (more efficient on browsers that
 * coalesce resize fires).
 */
const MOBILE_MAX_WIDTH = 640;

function detect(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= MOBILE_MAX_WIDTH;
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(detect);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const onChange = () => setIsMobile(detect());
    // Modern Safari needs the .addEventListener path; older fallback uses addListener
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    // Belt-and-suspenders: a plain resize listener catches DevTools
    // device-toolbar size changes that don't always fire matchMedia.
    window.addEventListener("resize", onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);

  return isMobile;
}
